import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ClipboardCheck } from 'lucide-react';
import { useUspsVetting } from './hooks/useUspsVetting';
import { UspsStatusSections, allRules } from './tabs/UspsReportBody';
import type { UspsVettingReport } from './tabs/uspsVetting.types';

/**
 * Sits on the main carrier screen, right under the carrier header: the USPS verdict, how many rules
 * match / do not match, and the first rules that do not. Click it to open the full list in place.
 * It says "not all rules confirmed" while any rule is unconfirmed, and shows nothing if the report
 * cannot be loaded (the USPS Vetting tab explains errors; this panel must not add noise).
 */
const STYLE: Record<UspsVettingReport['verdict']['status'], { box: string; label: string }> = {
  FAIL: { box: 'border-red-200 bg-red-50 text-red-800', label: 'Does not meet USPS vetting' },
  REVIEW: { box: 'border-yellow-200 bg-yellow-50 text-yellow-800', label: 'USPS vetting: no failures found, not all rules confirmed' },
  PASS: { box: 'border-green-200 bg-green-50 text-green-800', label: 'Meets USPS vetting' },
};

const SHOW_FAILS = 3;

export const UspsVettingPanel: React.FC<{ dotNumber: string }> = ({ dotNumber }) => {
  const { report, loading, error } = useUspsVetting(dotNumber);
  const [open, setOpen] = useState(false);

  if (loading) return <div className="mb-4 h-14 animate-pulse rounded-md bg-gray-100" aria-label="Checking USPS vetting rules" />;
  if (error || !report) return null;

  const style = STYLE[report.verdict.status];
  const s = report.summary;
  const fails = allRules(report).filter((r) => r.status === 'FAIL');

  return (
    <section className={`mb-4 rounded-md border ${style.box}`} aria-label="USPS vetting">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="flex w-full items-start gap-3 px-4 py-3 text-left">
        <ClipboardCheck className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{style.label}</span>
          <span className="block text-sm tabular-nums">
            {s.pass} match · {s.fail} do not match · {s.review + s.notEvaluated} could not be confirmed
          </span>
          {fails.length > 0 && (
            <span className="mt-1 block text-sm">
              Not matching: {fails.slice(0, SHOW_FAILS).map((r) => r.text).join('; ')}
              {fails.length > SHOW_FAILS ? `; +${fails.length - SHOW_FAILS} more` : ''}
            </span>
          )}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-sm font-semibold underline underline-offset-2">
          {open ? 'Hide' : 'See what matches'}
          {open ? <ChevronUp className="h-4 w-4" aria-hidden="true" /> : <ChevronDown className="h-4 w-4" aria-hidden="true" />}
        </span>
      </button>
      {open && (
        <div className="border-t border-current/20 bg-white px-4 py-3 text-gray-900">
          <UspsStatusSections report={report} />
          <p className="mt-3 text-xs text-gray-500">
            {report.standard}. Safety scores come from this platform&apos;s own model (USPS prefers CAB or Highway). Computed {new Date(report.computedAt).toLocaleString()}.
          </p>
        </div>
      )}
    </section>
  );
};

export default UspsVettingPanel;
